/**
 * Public web URL map (spec 15.15 - 15.18).
 *
 * React Navigation, given no path for a screen, derives one from the screen
 * name and nests it under every navigator above it - which is how the address
 * bar ends up reading
 *
 *   /DashboardStack/dashboard
 *   /ProductStack/ProductList?isRead=true&isWrite=true
 *
 * Internal stack and screen names are an implementation detail; they are not a
 * public URL structure, and the permission flags have no business being in a
 * URL at all. This module maps the existing navigation tree onto clean paths
 * without renaming a single screen (15.17) - the names on the left are exactly
 * the ones the stacks already declare.
 *
 * Only linking.web.ts imports this. Metro never resolves it, so Android's
 * deep-link behaviour is untouched.
 *
 * A stack that appears without a path of its own contributes nothing to the
 * URL, which is what lets a screen nested two navigators deep still live at a
 * single top-level path such as /products.
 *
 * Every path here is unique. Several screen *names* legitimately repeat across
 * stacks (AddEditCustomer lives in three, ViewDiscount in four), so each one is
 * given a path that says which flow it belongs to - otherwise a pasted URL
 * would be ambiguous on the way back in.
 */

export const webScreenPaths = {
  /* ---------------- auth (AuthStack) ---------------- */
  SignIn: 'sign-in',
  Register: 'register',
  ForgotPin: 'forgot-pin',
  /* changePin / setPin: the reset-PIN and set-PIN email link spellings. */
  ChangePin: {path: 'change-pin', alias: ['changePin', 'setPin']},
  UpdatePin: 'update-pin',
  /** The PIN screen an already-known user returns to; "unlock" says why. */
  LoginPin: 'unlock',

  /* ---------------- drawer (DrawerStack) ---------------- */
  DashboardStack: {
    screens: {
      dashboard: 'dashboard',
      ProductReport: 'reports/sold-products',
      issuedGiftCardReport: 'reports/issued-gift-cards',
      TaxReport: 'reports/tax',
      purchasedGiftCardReport: 'reports/purchased-gift-cards',
      PaymentReport: 'reports/payments',
      RevenueReport: 'reports/revenue',
      OrderReport: 'reports/orders',
      StaffReport: 'reports/staff',
      CustomerReport: 'reports/customers',
      OfferReport: 'reports/offers',
    },
  },

  SellScreenStack: {
    screens: {
      NewSellScreen: 'sell',
      /*
       * The cart is a pane inside the sell screen rather than a route of its
       * own, so there is no /cart to map; OrderSummary is the checkout step
       * the spec asks for at /checkout.
       */
      OrderSummary: 'checkout',
      ViewCustomerDetails: 'sell/customer',
      AddEditCustomer: 'sell/customer/edit',
      OrderList: {path: 'sell/orders', parse: {isPos: parseBool}},
      ProductDetails: 'sell/product',
      OrderDetails: {path: 'sell/order', parse: {isPos: parseBool}},
    },
  },

  ProductStack: {
    screens: {
      ProductList: 'products',
      AddEditProduct: {
        path: 'products/edit',
        screens: {
          General: '',
          Variant: 'variants',
          Modifier: 'modifiers',
        },
      },
      InventoryAdjustmentHistory: 'products/inventory-history',
      BulkEdit: 'products/bulk-edit',
      ReadymadeProducts: 'products/readymade',
    },
  },

  OrdersStack: {
    screens: {
      OrderList: {
        path: 'orders',
        screens: {
          'Pos Orders': '',
          'Online Orders': 'online',
        },
      },
      RestoreOrderList: 'orders/restore',
      // isPos picks the POS vs online order APIs - it must survive a refresh.
      OrderDetails: {path: 'orders/details', parse: {isPos: parseBool}},
      NewSellScreen: 'orders/sell',
    },
  },

  CategoryStack: {
    screens: {
      CategoryList: 'categories',
      AddEditCategories: {
        path: 'categories/edit',
        screens: {
          Category: '',
          Products: 'products',
        },
      },
      CategoryProducts: 'categories/products',
    },
  },

  InventoryStack: {screens: {ProductInventory: 'inventory'}},

  BrandStack: {
    screens: {
      BrandList: 'brands',
      AddEditBrand: {
        path: 'brands/edit',
        screens: {
          Brands: '',
          ProductsTap: 'products',
        },
      },
      AllProductList: 'brands/products',
    },
  },

  OptionStack: {
    screens: {
      OptionList: 'options',
      AddEditOption: 'options/edit',
      SortOptionList: 'options/sort',
    },
  },

  ModifierScreen: {
    path: 'modifiers',
    screens: {
      'Modifier Options': '',
      'Modifier Set': 'sets',
    },
  },

  ImageGalleryStack: {
    screens: {
      ImageGalleryScreen: {
        path: 'image-gallery',
        screens: {Image: '', Video: 'videos'},
      },
    },
  },

  SettingStack: {
    screens: {
      SettingScreen: 'settings',
      DisplaySettings: 'settings/display',
      StoreSetting: 'settings/store',
      ReceiptsSettings: 'settings/receipts',
      OrderSettings: 'settings/orders',
      ProductSettings: 'settings/products',
      LocationStack: {
        screens: {
          LocationList: 'settings/locations',
          AddEditLocation: 'settings/locations/edit',
        },
      },
      ZoneStack: {
        screens: {
          ZoneList: 'settings/zones',
          addUpdateZone: 'settings/zones/edit',
        },
      },
    },
  },

  TaxStack: {
    screens: {
      TaxList: 'taxes',
      AddEditTax: {
        path: 'taxes/edit',
        screens: {
          Tax: '',
          Products: 'products',
        },
      },
      TaxProducts: 'taxes/products',
      ZoneStack: {
        screens: {ZoneList: 'taxes/zones', addUpdateZone: 'taxes/zones/edit'},
      },
    },
  },

  ManualDiscountStack: {
    screens: {
      ManualDiscount: 'discounts/manual',
      AddEditManual: 'discounts/manual/edit',
      ViewDiscount: 'discounts/manual/view',
    },
  },

  AutomaticDiscountStack: {
    screens: {
      AutomaticDiscount: 'discounts/automatic',
      AddEditAutomaticDiscount: 'discounts/automatic/edit',
      ViewDiscount: 'discounts/automatic/view',
    },
  },

  BuyXgetYDiscountStack: {
    screens: {
      BuyXgetYDiscount: 'discounts/buy-x-get-y',
      AddEditBuyXgetYDiscount: 'discounts/buy-x-get-y/edit',
      ViewDiscount: 'discounts/buy-x-get-y/view',
    },
  },

  PayXGetFreeStack: {
    screens: {
      PayXGetFree: 'discounts/pay-x-get-free',
      AddEditPayXGetFree: 'discounts/pay-x-get-free/edit',
      ViewDiscount: 'discounts/pay-x-get-free/view',
    },
  },

  CustomerStack: {
    screens: {
      CustomerScreen: {
        path: 'customers',
        screens: {
          'Active Customer': '',
          'InActive Customer': 'inactive',
        },
      },
      AddEditCustomer: {
        path: 'customers/edit',
        screens: {
          'Customer Details': '',
          'Contact Information': 'contact',
        },
      },
      CustomerHistory: {
        /*
         * A path segment, not a query param: this screen has child tabs, and
         * React Navigation gives query params to the focused child tab (and
         * drops a parent's extra params from the URL), while CustomerHistory
         * itself is what reads customerHistoryData.
         */
        path: 'customers/history/:customerHistoryData?',
        stringify: {customerHistoryData: stringifyCustomerParam},
        parse: {customerHistoryData: parseCustomerParam},
        screens: {
          'Admin History': '',
          'POS History': 'pos',
        },
      },
      PrintMembershipCard: 'customers/membership-card',
      PrintCustomer: {
        path: 'customers/print',
        stringify: {customerHistoryData: stringifyCustomerParam},
        parse: {customerHistoryData: parseCustomerParam},
      },
    },
  },

  CustomerGroupStack: {
    screens: {
      CustomerGroupList: 'customer-groups',
      AddEditCustomerGroups: 'customer-groups/edit',
    },
  },

  RoleStack: {screens: {RoleList: 'roles', AddRole: 'roles/edit'}},

  StaffStack: {
    screens: {
      StaffScreen: {
        path: 'staff',
        screens: {
          ActiveStaffList: '',
          InActiveStaffList: 'inactive',
        },
      },
      AddEditStaff: 'staff/edit',
      StaffLoginHistory: 'staff/login-history',
      AddRole: 'staff/roles/edit',
    },
  },

  GiftStack: {
    screens: {GiftCardList: 'gift-cards', AddEditGiftCard: 'gift-cards/edit'},
  },

  PurchaseGiftCardStack: {
    screens: {
      PurchasedGiftCardList: 'gift-cards/purchased',
      PurchaseGiftCardDetails: 'gift-cards/purchased/details',
    },
  },

  IssueGiftStack: {
    screens: {
      IssueGiftCard: 'gift-cards/issued',
      AddEditCustomer: 'gift-cards/issued/customer',
    },
  },

  ZoneStack: {screens: {ZoneList: 'zones', addUpdateZone: 'zones/edit'}},

  ProfileStack: {
    screens: {
      AccountSettings: 'account',
      MyAccount: 'account/profile',
      ActivityLogs: 'account/activity',
      HelpCenter: 'help',
    },
  },

  InvitedStaff: 'invited-staff',

  /** The drawer-level copy, reached from the sell screen's customer flow. */
  AddEditCustomer: 'customer/edit',
};

/**
 * Params that describe *permission*, not *location*, and must therefore never
 * reach the address bar (15.16, 15.24).
 *
 * The drawer passes these when it navigates
 * (customDrawer.tsx: navigate(route, {isRead, isWrite})) because each screen
 * reads them to decide whether to render its write controls. That is fine as
 * navigation state; it is not fine as a public URL, where it both leaks the
 * permission model and invites someone to type isWrite=true by hand.
 */
export const PERMISSION_PARAMS = ['isRead', 'isWrite'];

/**
 * Params that the path already says, kept out of the URL for tidiness only.
 *
 * The orders screen hosts two tabs whose navigator supplies
 * `initialParams={{isPos: true}}` / `{{isPos: false}}`, so React Navigation
 * serialises the flag into the address bar and /orders reads
 * /orders?isPos=true. The tab is already in the path, and the navigator
 * re-applies its initialParams whenever a route is created from a URL, so
 * dropping the query loses nothing.
 *
 * Unlike PERMISSION_PARAMS this is removed on the way *out* only. There is no
 * security argument for filtering it on the way in - it is ordinary
 * navigation state - and filtering inbound would break a hand-written link
 * that legitimately carries it.
 *
 * Only params that no route *reads* are listed. The staff tabs pass
 * isActive the same way, but AddEditStaff reads Route.params.isActive on a
 * different route, so that one is deliberately left in the URL rather than
 * risk filtering it out from under a screen that wants it.
 *
 * isPos is the same case: OrderDetails and the sell screen's OrderList read
 * it to choose the POS or the online order APIs, so stripping it everywhere
 * sent a refreshed POS order to the online endpoints. It is dropped only on
 * the two orders-tab URLs, whose path already says which tab
 * (ORDERS_TAB_PATH / TAB_ONLY_URL_PARAMS, applied in linking.web.ts).
 */
export const REDUNDANT_URL_PARAMS = ['key'];

/** Dropped only from the orders-tab URLs (/orders, /orders/online). */
export const TAB_ONLY_URL_PARAMS = ['isPos'];
export const ORDERS_TAB_PATH = /^\/orders(\/online)?\/?(\?|#|$)/;

/** URL query values are strings; "false" would otherwise be truthy. */
function parseBool(value: string): boolean {
  return value === 'true';
}

/*
 * CustomerHistory and PrintCustomer receive the whole customer row as a
 * param. Serialised by default it became "[object Object]", so a refresh or a
 * pasted link showed "undefined undefined" and loaded history for no
 * customer. Only the fields those two screens read go into the URL.
 */
function stringifyCustomerParam(customer: any): string {
  if (!customer || typeof customer !== 'object') {
    return String(customer ?? '');
  }
  return JSON.stringify({
    customerId: customer.customerId,
    isWholesale: customer.isWholesale,
    isApproved: customer.isApproved,
    user: {
      firstName: customer.user?.firstName,
      lastName: customer.user?.lastName,
      createdAt: customer.user?.createdAt,
    },
  });
}

function parseCustomerParam(value: string): any {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}